import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Button, Input, Select, Modal, PageHeader, EmptyState, Badge } from "@/components/kit";
import InterestPicker from "@/components/InterestPicker";
import { TID } from "@/constants/testIds";
import { Plus } from "@phosphor-icons/react";

const PRODUCTS = [
  { value: "daily", label: "Daily Savings" },
  { value: "rd", label: "Recurring Deposit" },
  { value: "fd", label: "Fixed Deposit" },
];

export default function SavingsPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const customerIdFilter = params.get("customer_id");
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [product, setProduct] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product: "daily", interest_method: "SI", interest_rate: 4 });
  const [err, setErr] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    if (customerIdFilter) params.set("customer_id", customerIdFilter);
    if (product) params.set("product", product);
    const [a, c] = await Promise.all([
      api.get(`/savings?${params.toString()}`),
      api.get("/customers"),
    ]);
    setAccounts(a.data);
    setCustomers(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [product, customerIdFilter]);

  const canCreate = ["super_admin", "branch_admin", "staff"].includes(user.role);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const body = { ...form };
    ["interest_rate", "tenure_months", "installment_amount", "principal", "opening_balance", "maturity_reminder_days", "premature_penalty_pct"].forEach(k => {
      if (body[k] !== undefined && body[k] !== "") body[k] = Number(body[k]);
      else delete body[k];
    });
    ["opened_on", "account_no"].forEach(k => { if (!body[k]) delete body[k]; });
    try {
      await api.post("/savings", body);
      setOpen(false);
      setForm({ product: "daily", interest_method: "SI", interest_rate: 4 });
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed");
    }
  };

  const custName = (id) => customers.find((c) => c.id === id)?.name || "—";

  return (
    <div className="w-full p-4 md:p-8 pb-24 md:pb-8 fade-up">
      <PageHeader
        title="Savings"
        subtitle="Daily savings, recurring & fixed deposits with interest tracking."
        action={
          canCreate && (
            <Button onClick={() => setOpen(true)} data-testid={TID.addBtn("savings")}>
              <Plus size={16} weight="bold" /> New account
            </Button>
          )
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex gap-2 flex-wrap">
          {[["", "All"], ["daily", "Daily"], ["rd", "RD"], ["fd", "FD"]].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setProduct(v)}
              data-testid={`savings-filter-${v || "all"}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                product === v
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </Card>

      <Card>
        {accounts.length === 0 ? (
          <EmptyState title="No savings accounts" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold">Product</th>
                  <th className="text-left px-4 py-3 font-semibold">Rate</th>
                  <th className="text-right px-4 py-3 font-semibold">Balance</th>
                  <th className="text-right px-4 py-3 font-semibold">Maturity</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`savings-row-${a.id}`}>
                    <td className="px-4 py-3 font-medium">{custName(a.customer_id)}</td>
                    <td className="px-4 py-3"><Badge tone="info">{productLabel(a.product)}</Badge></td>
                    <td className="px-4 py-3 mono">{a.interest_rate}% · {a.interest_method}</td>
                    <td className="px-4 py-3 mono text-right text-emerald-700 font-medium">{money(a.balance)}</td>
                    <td className="px-4 py-3 mono text-right text-slate-600">{a.maturity_value ? money(a.maturity_value) : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/savings/${a.id}`} className="text-emerald-700 font-medium hover:underline" data-testid={`open-savings-${a.id}`}>Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Open savings account">
        <form onSubmit={submit} className="space-y-4">
          <Select
            label="Customer" required testId="savings-customer"
            value={form.customer_id || ""} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
          >
            <option value="">Select customer</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Product" required testId="savings-product"
              value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })}
            >
              {PRODUCTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Select>
            {form.product === "daily" && (
              <Input
                label="Opening balance (optional)" type="number" step="0.01" testId="savings-opening"
                value={form.opening_balance || ""} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
              />
            )}
            {form.product === "rd" && (
              <Input label="Tenure (months)" type="number" required testId="savings-tenure"
                value={form.tenure_months || ""} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} />
            )}
            {form.product === "fd" && (
              <Input label="Tenure (months)" type="number" required testId="savings-tenure"
                value={form.tenure_months || ""} onChange={(e) => setForm({ ...form, tenure_months: e.target.value })} />
            )}
          </div>
          {form.product === "rd" && (
            <Input label="Monthly installment" type="number" step="0.01" required testId="savings-installment"
              value={form.installment_amount || ""} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} />
          )}
          {form.product === "fd" && (
            <Input label="Principal" type="number" step="0.01" required testId="savings-principal"
              value={form.principal || ""} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
          )}

          {/* Legacy-entry row — leave blank for a fresh account */}
          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-100">
            <Input label="Opening date (optional, for legacy)" type="date" testId="savings-opened-on"
              value={form.opened_on || ""} onChange={(e) => setForm({ ...form, opened_on: e.target.value })} />
            <Input label="Account no. (optional, for legacy)" placeholder="e.g. FD-2023-045" testId="savings-account-no"
              value={form.account_no || ""} onChange={(e) => setForm({ ...form, account_no: e.target.value })} />
          </div>
          <div className="text-[11px] text-slate-500 -mt-1">
            For old deposits: enter the real opening date — maturity date & interest are computed from this. Leave blank for a brand-new account.
          </div>

          <InterestPicker
            value={{ method: form.interest_method, rate: form.interest_rate }}
            onChange={(v) => setForm({ ...form, interest_method: v.method, interest_rate: v.rate })}
            previewKind={form.product === "fd" ? "fd" : form.product === "rd" ? "rd" : null}
            previewParams={{
              principal: form.principal,
              installment: form.installment_amount,
              tenure_months: form.tenure_months,
            }}
          />

          {(form.product === "rd" || form.product === "fd") && (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Maturity reminder (days before)"
                type="number" min="0" testId="savings-reminder-days"
                value={form.maturity_reminder_days ?? 7}
                onChange={(e) => setForm({ ...form, maturity_reminder_days: e.target.value })}
              />
              <Input
                label="Pre-mature penalty %  (optional)"
                type="number" step="0.01" testId="savings-premature-pct"
                value={form.premature_penalty_pct ?? ""}
                onChange={(e) => setForm({ ...form, premature_penalty_pct: e.target.value })}
              />
            </div>
          )}

          {err && <div className="text-sm text-rose-600">{String(err)}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" type="button" onClick={() => setOpen(false)} data-testid={TID.cancelBtn}>Cancel</Button>
            <Button type="submit" data-testid={TID.saveBtn}>Create</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
const productLabel = (p) => ({ daily: "Daily", rd: "RD", fd: "FD" }[p] || p);
